import React, { useContext, useEffect, useState } from 'react'
import { renderRichText } from 'gatsby-source-contentful/rich-text'
import TextLink from '@toasttab/shared/src/components/molecules/TextLink/TextLink'
import { usePageContext } from '@toasttab/shared/src/hooks/usePageContext'
import {
  getIsActiveDate,
  getIsActivePage
} from '@toasttab/shared/src/utils/bannerUtils'
import { GlobalBannerData, BannerTheme } from './GlobalBanner.d'
// eslint-disable-next-line no-restricted-imports
import { ReactComponent as CloseIconLight } from '@toasttab/shared/src/assets/svg/icon-close-light.svg'
// eslint-disable-next-line no-restricted-imports
import { ReactComponent as CloseIconDark } from '@toasttab/shared/src/assets/svg/icon-close.svg'
import { BannerTextOptions } from '@toasttab/shared/src/utils/Contentful/richText'
import { localizeLink } from '@toasttab/shared/src/utils/linkUtils'
import { I18nextContext } from '@toasttab/shared/src/context/i18nextContext'

const themeStyles: Record<BannerTheme, { background: string; text: string }> = {
  green: {
    background: 'bg-grass-25',
    text: 'text-black'
  },
  yellow: {
    background: 'bg-yellow-25',
    text: 'text-black'
  },
  charcoal: {
    background: 'bg-gray-125',
    text: 'text-white'
  },
  blue: {
    background: 'bg-blue-0',
    text: 'text-black'
  },
  gradient: {
    background:
      'bg-utility-banner-gradient-mobile lg:bg-utility-banner-gradient',
    text: 'text-black'
  },
  default: {
    background:
      'bg-utility-banner-gradient-mobile lg:bg-utility-banner-gradient',
    text: 'text-black'
  }
}

const GlobalBanner = ({ bannerData }: { bannerData?: GlobalBannerData }) => {
  const localizationContext = useContext(I18nextContext)
  const [activeBanner, setActiveBanner] = useState<GlobalBannerData | null>(
    null
  )
  const {
    activeGlobalBanner,
    globalSettings,
    headerHeight,
    region,
    setActiveGlobalBanner,
    setHeaderHeight
  } = usePageContext()

  useEffect(() => {
    // Allow for custom banner to be passed via props that will override any Contentful settings
    if (bannerData) {
      setActiveBanner(bannerData)
    } else {
      // Filter to active entries only for the correct region
      const activeBannerData = globalSettings?.globalBanner.filter(
        ({ activePages, country, endDate, startDate }: GlobalBannerData) =>
          country === region &&
          getIsActiveDate(startDate, endDate) &&
          getIsActivePage(activePages)
      )

      // If we have an active banner, save the first one in state
      if (
        activeBannerData?.length > 0 &&
        sessionStorage.getItem(activeBannerData[0].contentful_id) !== 'true'
      ) {
        setActiveBanner(activeBannerData[0])
      } else {
        setActiveBanner(null)
      }
    }
  }, [bannerData, globalSettings?.globalBanner, region, setActiveBanner])

  useEffect(() => {
    if (activeBanner && activeBanner.contentful_id !== activeGlobalBanner) {
      // Save active banner ID to global context
      setActiveGlobalBanner(activeBanner.contentful_id)
    }
  }, [activeBanner, activeGlobalBanner, setActiveGlobalBanner])

  const handleClose = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ): void => {
    event.preventDefault()
    setActiveBanner(null)
    setActiveGlobalBanner(null)
    sessionStorage.setItem(activeBanner.contentful_id, 'true')

    // Re-set headerHeight so the mobile nav positioning adjusts when the banner is closed
    setTimeout(() => {
      const headHeight =
        typeof document !== 'undefined'
          ? document.querySelector('.headerContent')?.clientHeight
          : 0
      if (headHeight > 0 && headHeight !== headerHeight) {
        setHeaderHeight(headHeight)
      }
    }, 50)
  }
  const normalizedTheme = (activeBanner?.theme?.toLowerCase() ||
    'default') as BannerTheme
  const currentTheme = themeStyles[normalizedTheme]
  return (
    <>
      {activeBanner ? (
        <div className='overflow-hidden' data-testid='GlobalBanner'>
          <div
            className={`relative ${currentTheme.background} md:h-10 min-h-[40px] text-base`}
          >
            <a
              className='py-2 pl-4 md:pl-0 flex md:justify-center md:items-center md:h-full'
              data-testid='GlobalBanner-link'
              href={
                activeBanner.cta?.url
                  ? localizeLink(
                      activeBanner.cta?.url,
                      localizationContext.language,
                      localizationContext
                    )
                  : ''
              }
            >
              <div className='flex justify-start md:justify-center items-center h-full text-left md:text-center md:items-start'>
                <div className='flex items-start w-full flex-col md:flex-row md:justify-center md:items-center pr-10'>
                  <div
                    className={`flex items-start w-full md:justify-center ${
                      activeBanner.badgeIcon?.desktop?.file?.url
                        ? 'gap-1'
                        : 'gap-0'
                    }`}
                  >
                    {activeBanner.badgeIcon?.desktop?.file?.url ? (
                      <img
                        alt={activeBanner.badgeIcon.title || ''}
                        className='w-5 h-5 flex-shrink-0 self-start mt-[2px]'
                        src={activeBanner.badgeIcon.desktop.file.url}
                      />
                    ) : null}
                    <div className={`${currentTheme.text}`}>
                      {renderRichText(activeBanner.heading, BannerTextOptions)}
                    </div>
                  </div>
                  {activeBanner.subheading?.raw ? (
                    <div
                      className={`flex items-center ${
                        activeBanner.badgeIcon?.desktop?.file?.url
                          ? 'gap-1 md:gap-0'
                          : 'gap-0'
                      }`}
                    >
                      {activeBanner.badgeIcon?.desktop?.file?.url ? (
                        <div
                          aria-hidden
                          className='w-5 flex-shrink-0 md:hidden'
                        />
                      ) : null}
                      <TextLink
                        classes='ml-0 md:ml-2 whitespace-nowrap leading-none !text-[14px]'
                        el='div'
                        text={renderRichText(activeBanner.subheading) as string}
                        underline={true}
                        color={
                          (activeBanner.theme === null
                            ? 'dark'
                            : normalizedTheme === 'charcoal'
                            ? 'light'
                            : 'default') as 'default' | 'light' | 'dark'
                        }
                        icon={
                          activeBanner.theme !== null ? 'arrowRight' : undefined
                        }
                      />
                    </div>
                  ) : null}
                </div>
              </div>
            </a>
            <button
              aria-label='Close banner'
              className='absolute top-1 right-1'
              onClick={handleClose}
            >
              {(normalizedTheme || 'default') === 'charcoal' ? (
                <CloseIconLight height='30' width='30' />
              ) : (
                <CloseIconDark height='30' width='30' />
              )}
            </button>
          </div>
        </div>
      ) : null}
    </>
  )
}

export default GlobalBanner
